import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../controls/modals/ConfirmModal";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import {
  CATALOG_MOLD_SIZE_INITIAL_VALUES,
  CatalogMoldSize,
} from "../../../models/CatalogMoldSize";
import {
  deleteCatalogMoldSize,
  upsertCatalogMoldSize,
} from "../../../repositories/CatalogMoldSizeRepository";
import { useCatalogMoldSizeContext } from "../context/CatalogMoldSizeContext";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  moldSize: CatalogMoldSize | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
  refreshData: Function;
};
export const MoldSizeModal = ({
  moldSize,
  showModal,
  onHide,
  refreshData,
}: Props) => {
  const { state } = useCatalogMoldSizeContext();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingMoldSize, setExistingMoldSize] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatalogMoldSize>({
    defaultValues: CATALOG_MOLD_SIZE_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const onSubmit: SubmitHandler<CatalogMoldSize> = async (
    data: CatalogMoldSize,
  ) => {
    setIsSubmitLoading(true);
    const moldSizes: CatalogMoldSize[] = state.moldSizes || [];
    if (showModal === "delete") {
      setIsSubmitLoading(true);
      const resp = await deleteCatalogMoldSize(moldSize?.MoldSizeId ?? null);
      if (resp.ok) {
        await refreshData();
        onHide({ shouldUpdate: true });
        setNotificationMessage("The moldSize was deleted successfully");
      } else {
        setError(resp.message);
      }
      setIsSubmitLoading(false);
    } else {
      if (
        moldSizes.some(
          (moldSize) =>
            moldSize.MoldSizeName === data.MoldSizeName &&
            moldSize.MoldSizeId !== data.MoldSizeId,
        )
      ) {
        setExistingMoldSize(data?.MoldSizeName || "");
        setConfirmOpen(true);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatalogMoldSize) => {
    const resp = await upsertCatalogMoldSize(data);
    if (resp.ok) {
      const entityId = get(resp, "data.returnValue", 0);
      await refreshData();
      setNotificationMessage("The mold size was saved successfully");
      onHide({ entityId: entityId, shouldUpdate: true });
    } else {
      setError(resp.message);
      onHide({ shouldUpdate: false });
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(moldSize || CATALOG_MOLD_SIZE_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <ConfirmModal
        show={confirmOpen}
        onHide={(proceed: boolean) => {
          if (proceed) {
            performUpsert(getValues());
            onHide({ shouldUpdate: true });
          }
          setConfirmOpen(false);
          setIsSubmitLoading(false);
        }}
        existingName={existingMoldSize}
        label="MoldSize"
      />
      <MesfModal
        title={`${title} MOLD SIZE`}
        open={showModal != ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="mold-size-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="MoldSizeName"
                  rules={{
                    required: "Name is required",
                  }}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Name"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="Height"
                  rules={{
                    required: "Height is required",
                  }}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Height"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      type="number"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="Width"
                  rules={{
                    required: "Width is required",
                  }}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Width"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      type="number"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="MinSpeedLimit"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Min Speed"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      type="number"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="LbsPerInch"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Lbs Per Inch"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      type="number"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="Code"
                  control={control}
                  rules={{
                    required: "Code is required",
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Code"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      type="number"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      inputProps={{ min: 0 }}
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Controller
                  name="Enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Mold Size"
      />
    </>
  );
};
